import { ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';

import { noopFunction } from '@vestico/utils';

import { MatchResponse } from './MatchContext';

// Registry to store all MatchContext instances by product UID
const matchContextRegistry = new Map<string, MatchResponse>();

interface MatchContextRegistryContextType {
  registerMatchContext: (productUID: string, context: MatchResponse) => void;
  unregisterMatchContext: (productUID: string) => void;
  getMatchContext: (productUID: string) => MatchResponse | undefined;
  clearMatchContextRegistry: () => void;
  registeredProductUIDs: string[];
}

const MatchContextRegistryContext = createContext<MatchContextRegistryContextType | null>(null);
interface MatchContextRegistryProviderProps {
  children: ReactNode;
}

const emptyMatchContext: MatchResponse = {
  activeMatchingType: 'size-rec',
  defaultMatchingType: 'size-rec',
  onActiveMatchingTypeChange: noopFunction,
  hasContentMatchAvailable: false,
  hasSizeRecAvailable: false,
  responses: undefined,
  sizeRecommendation: undefined,
  selectedResponseIndex: undefined,
  onSelectedReponseIndex: noopFunction,
};

export const MatchContextRegistryProvider = ({ children }: MatchContextRegistryProviderProps) => {
  const [registeredProductUIDs, setRegisteredProductUIDs] = useState<string[]>([]);

  const registerMatchContext = useCallback((productUID: string, context: MatchResponse) => {
    console.debug(`register context: ${productUID}`);
    matchContextRegistry.set(productUID, context);
    setRegisteredProductUIDs((prev) => [...new Set([...prev, productUID])]);
  }, []);

  const unregisterMatchContext = useCallback((productUID: string) => {
    console.debug(`unregister context: ${productUID}`);
    matchContextRegistry.delete(productUID);
    setRegisteredProductUIDs((prev) => prev.filter((uid) => uid !== productUID));
  }, []);

  const getMatchContext = useCallback((productUID: string): MatchResponse | undefined => {
    console.debug(`get context: ${productUID}`);
    return matchContextRegistry.get(productUID);
  }, []);

  const clearMatchContextRegistry = useCallback(() => {
    matchContextRegistry.clear();
    setRegisteredProductUIDs([]);
  }, []);

  const value: MatchContextRegistryContextType = useMemo(() => {
    return {
      registerMatchContext,
      unregisterMatchContext,
      getMatchContext,
      clearMatchContextRegistry,
      registeredProductUIDs,
    };
  }, [registerMatchContext, unregisterMatchContext, getMatchContext, clearMatchContextRegistry, registeredProductUIDs]);

  return <MatchContextRegistryContext.Provider value={value}>{children}</MatchContextRegistryContext.Provider>;
};

export const useMatchContextRegistry = ({ productUID }: { productUID?: string }) => {
  const context = useContext(MatchContextRegistryContext);
  if (!context) {
    throw new Error('useMatchContextRegistry must be used within a MatchContextRegistryProvider');
  }

  const activeMatchContext = useMemo(() => {
    if (!productUID) {
      return emptyMatchContext;
    }
    return context.getMatchContext(productUID) ?? emptyMatchContext;
  }, [productUID, context]);

  return { context, activeMatchContext };
};
